import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Trash2, CheckCircle2, Home, Palette, CalendarDays, Clock3, MapPin, BadgeDollarSign, Users, Languages, Heart, Clock4, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";

const C = { navy: "#123B6D", teal: "#26A6A1", purple: "#7A3E9D", orange: "#F59C1A", yellow: "#F7C948", green: "#74B843" };
const PRICE = 25;
const PAYPAL_LINK = "https://www.paypal.com/ncp/payment/6GXGY7JN6PU22";

const T = {
  en: {
    switch: "Espa\u00F1ol",
    badge: "Bilingual Exploradores \u00D7 Celi's Collection",
    title: "CREATE & CONNECT",
    subtitle: "Bilingual Hat Decorating Workshop",
    tagline: "Create. Connect. Explore. \u{1F30E}",
    heroP: "Join us for a fun bilingual creative experience where children will design and decorate their own hat while experiencing Spanish naturally through creativity and community.",
    chips: [
      { Icon: CalendarDays, text: "Saturday, October 3, 2026" },
      { Icon: Clock3, text: "1:00\u20133:00 PM" },
      { Icon: MapPin, text: "Alma y Lengua Bilingual Learning Center" },
      { Icon: BadgeDollarSign, text: "$25 per child" },
      { Icon: Users, text: "Only 20 child spots available" },
      { Icon: Heart, text: "Family-friendly" },
      { Icon: Languages, text: "Spanish-led with English support" },
      { Icon: Palette, text: "No previous Spanish experience needed" },
    ],
    spotsLeft: (n) => `${n} of 20 spots remaining`,
    full: "THIS EVENT IS FULL",
    fullNote: "All 20 spots are filled. Join the waitlist below and we'll contact you if a spot opens.",
    formTitle: "Registration Form",
    s1: "Section 1 \u2014 Child Information",
    childName: "Child's Full Name", childAge: "Child's Age", allergies: "Allergies",
    addChild: "Add Another Child", remove: "Remove",
    s2: "Section 2 \u2014 Parent/Guardian Information",
    parentName: "Parent/Guardian Full Name", email: "Email Address", phone: "Phone Number",
    s3: "Section 3 \u2014 Attendance",
    attendanceQ: "Will you be staying during the workshop or dropping off your child?",
    stay: "I will stay with my child/children.",
    dropoff: "I plan to drop off my child/children.",
    emergencyName: "Emergency Contact Name", emergencyPhone: "Emergency Contact Phone",
    s4: "Section 4 \u2014 Allergies & Important Information",
    allergyQ: "Does your child have any food allergies, medical dietary restrictions, or other allergies we should know about?",
    no: "No", yes: "Yes", explain: "Please explain",
    anythingElse: "Is there anything else you would like us to know to help your child have a positive experience?",
    s5: "Section 5 \u2014 Payment",
    feeLabel: "Registration Fee", totalLabel: "Total Due",
    perChild: (n) => `$25 \u00D7 ${n} ${n === 1 ? "child" : "children"}`,
    consent: "I confirm that the information provided above is accurate and understand that registration is not confirmed until payment is completed.",
    payLabel: "Pay with PayPal & Complete Registration",
    payNote: "Your child's spot is only confirmed once the PayPal payment is completed.",
    paySetup: "Online payment is being set up. Please check back soon or contact us at 856-344-3497 to register.",
    joinWaitlist: "Join the Waitlist", sending: "Sending...",
    errRequired: "Please complete all required fields (including each child's name and age) and check the confirmation box.",
    errEmail: "Please enter a valid email address.",
    errEmergency: "Please provide an emergency contact name and phone for drop-off.",
    errAllergy: "Please explain the allergies.",
    errGeneric: "Something went wrong with the payment. Please try again or call 856-344-3497.",
    successTitle: "\u{1F389} You're officially registered!",
    successBody: "We can't wait to Create & Connect with your Explorador!",
    successDate: "Saturday, October 3", successTime: "1:00\u20133:00 PM",
    successLoc: "Alma y Lengua Bilingual Learning Center",
    successEmail: "A confirmation has also been sent to your email.",
    paidLabel: "Amount paid", txnLabel: "PayPal confirmation",
    waitSuccessTitle: "You're on the waitlist!",
    waitSuccessBody: "The workshop is currently full, so we've added you to the waitlist. We'll contact you right away if a spot opens \u2014 no payment is needed while you wait.",
    paidWaitTitle: "Payment received \u2014 event is full",
    paidWaitBody: "Your payment was received, but the last spot was just taken. We've notified the Alma y Lengua team \u2014 they will contact you right away to offer priority on the waitlist or a full refund.",
    pendingTitle: "Almost done \u2014 complete your payment!",
    pendingBody: "Your registration has been received. To secure your child's spot, please complete your payment now:",
    payNow: (amt) => `Pay $${amt} with PayPal`,
    quantityNote: (n, amt) => n > 1
      ? `On the PayPal page, set the quantity to ${n} so your total is $${amt}.`
      : "On the PayPal page, complete your $25 payment.",
    pendingConfirm: "Your child's spot is confirmed once we receive your payment \u2014 you'll get a confirmation email. These instructions were also sent to your email.",
    returnHome: "Return to Home Page",
  },
  es: {
    switch: "English",
    badge: "Bilingual Exploradores \u00D7 Celi's Collection",
    title: "CREA Y CONECTA",
    subtitle: "Taller Biling\u00FCe de Decoraci\u00F3n de Gorras",
    tagline: "Crea. Conecta. Explora. \u{1F30E}",
    heroP: "Acomp\u00E1\u00F1anos para una experiencia creativa y biling\u00FCe donde los ni\u00F1os dise\u00F1ar\u00E1n y decorar\u00E1n su propia gorra mientras experimentan el espa\u00F1ol de una manera natural a trav\u00E9s de la creatividad y la comunidad.",
    chips: [
      { Icon: CalendarDays, text: "S\u00E1bado, 3 de octubre de 2026" },
      { Icon: Clock3, text: "1:00\u20133:00 PM" },
      { Icon: MapPin, text: "Alma y Lengua Bilingual Learning Center" },
      { Icon: BadgeDollarSign, text: "$25 por ni\u00F1o(a)" },
      { Icon: Users, text: "Solo 20 lugares disponibles" },
      { Icon: Heart, text: "Ambiente familiar" },
      { Icon: Languages, text: "Dirigido en espa\u00F1ol con apoyo en ingl\u00E9s" },
      { Icon: Palette, text: "No se requiere experiencia previa en espa\u00F1ol" },
    ],
    spotsLeft: (n) => `Quedan ${n} de 20 lugares`,
    full: "ESTE EVENTO EST\u00C1 LLENO",
    fullNote: "Los 20 lugares est\u00E1n llenos. \u00DAnase a la lista de espera y le contactaremos si se abre un lugar.",
    formTitle: "Formulario de Inscripci\u00F3n",
    s1: "Secci\u00F3n 1 \u2014 Informaci\u00F3n del Ni\u00F1o(a)",
    childName: "Nombre completo del ni\u00F1o(a)", childAge: "Edad del ni\u00F1o(a)", allergies: "Alergias",
    addChild: "Agregar otro ni\u00F1o(a)", remove: "Quitar",
    s2: "Secci\u00F3n 2 \u2014 Informaci\u00F3n del Padre, Madre o Tutor",
    parentName: "Nombre completo del padre, madre o tutor", email: "Correo electr\u00F3nico", phone: "N\u00FAmero de tel\u00E9fono",
    s3: "Secci\u00F3n 3 \u2014 Asistencia",
    attendanceQ: "\u00BFPermanecer\u00E1 durante el taller o dejar\u00E1 a su hijo(a)?",
    stay: "Permanecer\u00E9 con mi hijo(a)/mis hijos.",
    dropoff: "Planeo dejar a mi hijo(a)/mis hijos.",
    emergencyName: "Nombre del contacto de emergencia", emergencyPhone: "Tel\u00E9fono del contacto de emergencia",
    s4: "Secci\u00F3n 4 \u2014 Alergias e Informaci\u00F3n Importante",
    allergyQ: "\u00BFSu hijo(a) tiene alguna alergia alimentaria, restricci\u00F3n alimentaria m\u00E9dica u otra alergia que debamos conocer?",
    no: "No", yes: "S\u00ED", explain: "Por favor explique",
    anythingElse: "\u00BFHay algo m\u00E1s que le gustar\u00EDa que supi\u00E9ramos para ayudar a que su hijo(a) tenga una experiencia positiva?",
    s5: "Secci\u00F3n 5 \u2014 Pago",
    feeLabel: "Costo de inscripci\u00F3n", totalLabel: "Total",
    perChild: (n) => `$25 \u00D7 ${n} ${n === 1 ? "ni\u00F1o(a)" : "ni\u00F1os"}`,
    consent: "Confirmo que la informaci\u00F3n proporcionada es correcta y entiendo que la inscripci\u00F3n no est\u00E1 confirmada hasta completar el pago.",
    payLabel: "Pagar con PayPal y Completar Inscripci\u00F3n",
    payNote: "El lugar de su hijo(a) solo se confirma una vez completado el pago de PayPal.",
    paySetup: "El pago en l\u00EDnea se est\u00E1 configurando. Vuelva pronto o ll\u00E1menos al 856-344-3497 para inscribirse.",
    joinWaitlist: "Unirse a la Lista de Espera", sending: "Enviando...",
    errRequired: "Por favor complete todos los campos requeridos (incluyendo el nombre y la edad de cada ni\u00F1o) y marque la casilla de confirmaci\u00F3n.",
    errEmail: "Por favor ingrese un correo electr\u00F3nico v\u00E1lido.",
    errEmergency: "Por favor proporcione el nombre y tel\u00E9fono del contacto de emergencia.",
    errAllergy: "Por favor explique las alergias.",
    errGeneric: "Algo sali\u00F3 mal con el pago. Int\u00E9ntelo de nuevo o llame al 856-344-3497.",
    successTitle: "\u{1F389} \u00A1Su inscripci\u00F3n est\u00E1 confirmada!",
    successBody: "\u00A1Estamos emocionados de Crear y Conectar con su Explorador(a)!",
    successDate: "S\u00E1bado, 3 de octubre", successTime: "1:00\u20133:00 PM",
    successLoc: "Alma y Lengua Bilingual Learning Center",
    successEmail: "Tambi\u00E9n hemos enviado una confirmaci\u00F3n a su correo electr\u00F3nico.",
    paidLabel: "Total pagado", txnLabel: "Confirmaci\u00F3n de PayPal",
    waitSuccessTitle: "\u00A1Est\u00E1 en la lista de espera!",
    waitSuccessBody: "El taller est\u00E1 lleno por el momento, as\u00ED que lo/la agregamos a la lista de espera. Le contactaremos de inmediato si se abre un lugar \u2014 no se necesita pago mientras espera.",
    paidWaitTitle: "Pago recibido \u2014 el evento est\u00E1 lleno",
    paidWaitBody: "Recibimos su pago, pero el \u00FAltimo lugar acaba de ocuparse. Hemos notificado al equipo de Alma y Lengua \u2014 se comunicar\u00E1n con usted de inmediato para ofrecerle prioridad en la lista de espera o un reembolso completo.",
    pendingTitle: "\u00A1Casi listo \u2014 complete su pago!",
    pendingBody: "Hemos recibido su inscripci\u00F3n. Para asegurar el lugar de su hijo(a), complete su pago ahora:",
    payNow: (amt) => `Pagar $${amt} con PayPal`,
    quantityNote: (n, amt) => n > 1
      ? `En la p\u00E1gina de PayPal, ajuste la cantidad a ${n} para que su total sea $${amt}.`
      : "En la p\u00E1gina de PayPal, complete su pago de $25.",
    pendingConfirm: "El lugar de su hijo(a) se confirma una vez que recibamos su pago \u2014 recibir\u00E1 un correo de confirmaci\u00F3n. Estas instrucciones tambi\u00E9n se enviaron a su correo.",
    returnHome: "Volver a la P\u00E1gina Principal",
  },
};

const inputCls = "w-full bg-white rounded-2xl border-2 border-slate-200 px-4 py-3 outline-none transition focus:border-[#26A6A1]";
const lbl = "block text-sm font-semibold text-slate-700 mb-2";

export default function Event() {
  const [lang, setLang] = useState("en");
  const tr = T[lang];
  useEffect(() => { document.title = "Create & Connect | Bilingual Hat Decorating Workshop"; }, []);

  const [children, setChildren] = useState([{ name: "", age: "", allergies: "" }]);
  const [d, setD] = useState({ parent_name: "", email: "", phone: "", attendance: "", emergency_name: "", emergency_phone: "", has_allergies: "", allergy_details: "", additional_info: "", consent: false });
  const [avail, setAvail] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | sending | success | waitlist-success | paid-waitlist | error
  const [confirmation, setConfirmation] = useState(null);
  const [showErr, setShowErr] = useState(false);
  const [payError, setPayError] = useState("");

  const refreshAvail = useCallback(() => {
    api.get("/event/availability").then(({ data }) => setAvail(data)).catch(() => {});
  }, []);
  useEffect(() => { refreshAvail(); }, [refreshAvail]);

  const total = PRICE * children.length;
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email);
  const childrenValid = children.every((c) => c.name.trim() && c.age !== "" && Number(c.age) >= 1 && Number(c.age) <= 17);
  const emergencyValid = d.attendance !== "dropoff" || (d.emergency_name.trim() && d.emergency_phone.trim());
  const allergyValid = d.has_allergies !== "yes" || d.allergy_details.trim();
  const valid = childrenValid && d.parent_name.trim() && emailValid && d.phone.trim() && d.attendance && d.has_allergies !== "" && emergencyValid && allergyValid && d.consent;
  const eventFull = !!avail?.full;

  const payload = () => ({
    children: children.map((c) => ({ name: c.name.trim(), age: Number(c.age), allergies: c.allergies.trim() })),
    parent_name: d.parent_name.trim(),
    email: d.email.trim(),
    phone: d.phone.trim(),
    attendance: d.attendance,
    emergency_name: d.attendance === "dropoff" ? d.emergency_name.trim() : "",
    emergency_phone: d.attendance === "dropoff" ? d.emergency_phone.trim() : "",
    has_allergies: d.has_allergies === "yes",
    allergy_details: d.has_allergies === "yes" ? d.allergy_details.trim() : "",
    additional_info: d.additional_info.trim(),
    consent: d.consent,
    language: lang,
  });

  const submitWaitlist = async () => {
    if (!valid) { setShowErr(true); document.getElementById("event-form").scrollIntoView({ behavior: "smooth" }); return; }
    setStatus("sending");
    try {
      await api.post("/event/waitlist", payload());
      setStatus("waitlist-success"); window.scrollTo({ top: 0, behavior: "smooth" });
    } catch { setStatus("error"); }
  };

  const submitRegistration = async () => {
    if (!valid) { setShowErr(true); document.getElementById("event-form").scrollIntoView({ behavior: "smooth" }); return; }
    setStatus("sending"); setPayError("");
    try {
      const { data } = await api.post("/event/register", payload());
      setConfirmation({ amount_due: data.amount_due, num_children: data.num_children, id: data.id });
      setStatus("pending");
      refreshAvail();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      if (err?.response?.status === 409) { refreshAvail(); setPayError(tr.fullNote); }
      else setPayError(tr.errGeneric);
      setStatus("idle");
    }
  };

  if (status === "pending" && confirmation) {
    return (
      <div className="min-h-screen grid place-items-center px-5 py-16 bg-white">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, ease: "backOut" }}
          className="max-w-xl w-full rounded-3xl shadow-2xl p-8 md:p-12 text-center border-4" style={{ borderColor: C.orange }} data-testid="event-pending">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: "spring" }}
            className="w-20 h-20 rounded-full grid place-items-center mx-auto mb-6 text-white" style={{ background: C.orange }}><BadgeDollarSign size={40} /></motion.div>
          <h1 className="text-2xl md:text-3xl font-extrabold mb-4" style={{ color: C.navy }} data-testid="event-pending-title">{tr.pendingTitle}</h1>
          <p className="text-slate-600 leading-relaxed mb-6">{tr.pendingBody}</p>
          <a href={PAYPAL_LINK} target="_blank" rel="noopener noreferrer" data-testid="event-paypal-link"
            className="inline-flex items-center justify-center gap-2 rounded-2xl px-8 py-5 font-extrabold text-white text-lg shadow-xl w-full mb-4" style={{ background: C.navy }}>
            {tr.payNow(Number(confirmation.amount_due || 0).toFixed(0))} <ExternalLink size={20} />
          </a>
          <p className="text-sm font-semibold rounded-2xl px-4 py-3 mb-4" style={{ background: "#FFF7EA", color: C.navy }} data-testid="event-quantity-note">
            {tr.quantityNote(confirmation.num_children || 1, Number(confirmation.amount_due || 0).toFixed(0))}
          </p>
          <p className="text-slate-500 text-sm mb-8">{tr.pendingConfirm}</p>
          <Link to="/" data-testid="event-return-home" className="inline-flex items-center gap-2 rounded-full px-8 py-4 font-bold text-white" style={{ background: C.teal }}><Home size={18} /> {tr.returnHome}</Link>
        </motion.div>
      </div>
    );
  }

  if (["success", "waitlist-success", "paid-waitlist"].includes(status)) {
    const isWait = status !== "success";
    return (
      <div className="min-h-screen grid place-items-center px-5 py-16 bg-white">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, ease: "backOut" }}
          className="max-w-xl w-full rounded-3xl shadow-2xl p-8 md:p-12 text-center border-4" style={{ borderColor: C.yellow }} data-testid="event-success">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: "spring" }}
            className="w-20 h-20 rounded-full grid place-items-center mx-auto mb-6 text-white" style={{ background: isWait ? C.orange : C.green }}>
            {isWait ? <Clock4 size={40} /> : <CheckCircle2 size={44} />}
          </motion.div>
          <h1 className="text-2xl md:text-3xl font-extrabold mb-4" style={{ color: C.navy }} data-testid="event-success-title">
            {status === "paid-waitlist" ? tr.paidWaitTitle : isWait ? tr.waitSuccessTitle : tr.successTitle}
          </h1>
          <p className="text-slate-600 leading-relaxed mb-2">{status === "paid-waitlist" ? tr.paidWaitBody : isWait ? tr.waitSuccessBody : tr.successBody}</p>
          {status === "success" && (
            <div className="my-6 rounded-2xl p-5 text-left text-sm space-y-1.5" style={{ background: "#F0FDFA" }} data-testid="event-confirmation-details">
              <p className="font-bold" style={{ color: C.navy }}>{tr.successDate} &middot; {tr.successTime}</p>
              <p className="text-slate-600">{tr.successLoc}</p>
              {confirmation && <p className="text-slate-600">{(confirmation.children || []).map((c) => c.name).join(", ")}</p>}
              {confirmation && <p className="text-slate-600">{tr.paidLabel}: <strong>${Number(confirmation.amount_due || 0).toFixed(2)}</strong></p>}
              {confirmation?.paypal_capture_id && <p className="text-slate-500 text-xs break-all">{tr.txnLabel}: {confirmation.paypal_capture_id}</p>}
            </div>
          )}
          {status === "success" && <p className="text-slate-500 text-sm mb-8">{tr.successEmail}</p>}
          {isWait && <div className="mb-8" />}
          <Link to="/" data-testid="event-return-home" className="inline-flex items-center gap-2 rounded-full px-8 py-4 font-bold text-white" style={{ background: C.teal }}><Home size={18} /> {tr.returnHome}</Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="fixed top-4 right-4 z-50">
        <button onClick={() => setLang(lang === "en" ? "es" : "en")} data-testid="event-lang" className="rounded-full px-4 py-2 text-sm font-bold text-white shadow-lg" style={{ background: C.navy }}>{tr.switch}</button>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${C.navy}, ${C.purple})` }}>
        <div className="max-w-4xl mx-auto px-5 py-16 md:py-20 text-center text-white relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-block rounded-full px-4 py-1.5 text-sm font-bold mb-6" style={{ background: C.yellow, color: C.navy }} data-testid="event-badge">{tr.badge}</span>
            <h1 className="text-4xl md:text-6xl font-extrabold leading-tight mb-3" data-testid="event-title">{tr.title}</h1>
            <p className="text-xl md:text-2xl font-bold mb-5" style={{ color: C.yellow }}>{tr.subtitle}</p>
            <p className="text-2xl font-extrabold mb-4">{tr.tagline}</p>
            <p className="text-lg text-white/90 max-w-2xl mx-auto">{tr.heroP}</p>
          </motion.div>
          <div className="flex flex-wrap justify-center gap-3 mt-10">
            {tr.chips.map(({ Icon, text }, i) => (
              <motion.div key={text} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 + i * 0.06 }}
                className="bg-white/95 rounded-full px-4 py-2.5 flex items-center gap-2 shadow-lg" data-testid={`event-chip-${i}`}>
                <Icon size={16} style={{ color: C.purple }} />
                <span className="text-xs font-bold" style={{ color: C.navy }}>{text}</span>
              </motion.div>
            ))}
          </div>
          {avail && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="mt-8">
              <span className="inline-block rounded-full px-5 py-2 text-sm font-extrabold shadow-lg" style={{ background: eventFull ? "#DC2626" : C.green, color: "#fff" }} data-testid="event-spots-badge">
                {eventFull ? tr.full : tr.spotsLeft(avail.spots_left)}
              </span>
            </motion.div>
          )}
        </div>
      </section>

      {/* Event flyer */}
      <section className="max-w-2xl mx-auto px-5 -mt-2 py-10">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <img
            src="https://customer-assets-eiarnc6j.emergentagent.net/job_alma-lengua-preview/artifacts/lcjd1uzh_ChatGPT%20Image%20Sep%2016%2C%202026%2C%2010_02_00%20AM.png"
            alt="Create & Connect - Bilingual Hat Decorating Workshop flyer"
            loading="lazy"
            data-testid="event-flyer"
            className="w-full rounded-3xl shadow-2xl border-4"
            style={{ borderColor: C.yellow }}
          />
        </motion.div>
      </section>

      {/* Form */}
      <section id="event-form" className="max-w-2xl mx-auto px-5 py-14">
        <h2 className="text-3xl font-extrabold text-center mb-8" style={{ color: C.navy }}>{tr.formTitle}</h2>
        {showErr && !valid && <div className="mb-6 rounded-2xl border-2 border-red-200 bg-red-50 text-red-700 px-5 py-4 text-sm" data-testid="event-error">{tr.errRequired}</div>}
        <form onSubmit={(e) => e.preventDefault()} noValidate className="space-y-8" data-testid="event-form-el">

          {/* Section 1 - Children */}
          <div className="bg-white rounded-3xl border-2 border-slate-100 shadow-sm p-6 md:p-8">
            <h3 className="font-bold text-lg mb-5" style={{ color: C.teal }}>{tr.s1}</h3>
            <div className="space-y-5">
              {children.map((c, i) => (
                <div key={i} className={i > 0 ? "pt-5 border-t-2 border-dashed border-slate-100" : ""}>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><label className={lbl}>{tr.childName} *</label><input data-testid={`event-child-name-${i}`} className={inputCls} value={c.name} onChange={(e) => setChildren(children.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} /></div>
                    <div><label className={lbl}>{tr.childAge} *</label><input type="number" min="1" max="17" data-testid={`event-child-age-${i}`} className={inputCls} value={c.age} onChange={(e) => setChildren(children.map((x, j) => j === i ? { ...x, age: e.target.value } : x))} /></div>
                  </div>
                  {i > 0 && (
                    <div className="mt-4 flex items-end gap-3">
                      <div className="flex-1"><label className={lbl}>{tr.allergies}</label><input data-testid={`event-child-allergies-${i}`} className={inputCls} value={c.allergies} onChange={(e) => setChildren(children.map((x, j) => j === i ? { ...x, allergies: e.target.value } : x))} /></div>
                      <button type="button" data-testid={`event-remove-child-${i}`} aria-label={`${tr.remove} ${i + 1}`} onClick={() => setChildren(children.filter((_, j) => j !== i))} className="text-red-500 pb-3"><Trash2 size={18} /></button>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-4 mt-5">
              <button type="button" data-testid="event-add-child" disabled={children.length >= 10} onClick={() => setChildren([...children, { name: "", age: "", allergies: "" }])} className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold text-white disabled:opacity-50" style={{ background: C.green }}><Plus size={16} /> {tr.addChild}</button>
              <span className="text-sm font-extrabold rounded-full px-4 py-2" style={{ background: "#FDF3E0", color: C.navy }} data-testid="event-running-total">{tr.perChild(children.length)} = ${total}</span>
            </div>
          </div>

          {/* Section 2 - Parent */}
          <div className="bg-white rounded-3xl border-2 border-slate-100 shadow-sm p-6 md:p-8">
            <h3 className="font-bold text-lg mb-5" style={{ color: C.purple }}>{tr.s2}</h3>
            <div className="space-y-4">
              <div><label className={lbl}>{tr.parentName} *</label><input data-testid="event-parent-name" className={inputCls} value={d.parent_name} onChange={(e) => setD({ ...d, parent_name: e.target.value })} /></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className={lbl}>{tr.email} *</label><input type="email" data-testid="event-email" className={inputCls} value={d.email} onChange={(e) => setD({ ...d, email: e.target.value })} />{showErr && !emailValid && <p className="text-xs text-red-500 mt-1">{tr.errEmail}</p>}</div>
                <div><label className={lbl}>{tr.phone} *</label><input type="tel" data-testid="event-phone" className={inputCls} value={d.phone} onChange={(e) => setD({ ...d, phone: e.target.value })} /></div>
              </div>
            </div>
          </div>

          {/* Section 3 - Attendance */}
          <div className="bg-white rounded-3xl border-2 border-slate-100 shadow-sm p-6 md:p-8">
            <h3 className="font-bold text-lg mb-2" style={{ color: C.orange }}>{tr.s3}</h3>
            <p className="text-sm font-semibold text-slate-700 mb-4">{tr.attendanceQ} *</p>
            <div className="space-y-3">
              {[["stay", tr.stay], ["dropoff", tr.dropoff]].map(([val, label]) => (
                <label key={val} data-testid={`event-attendance-${val}`} className="flex items-start gap-3 p-4 rounded-2xl border-2 cursor-pointer transition" style={{ borderColor: d.attendance === val ? C.orange : "#e2e8f0", background: d.attendance === val ? "#FFF7EA" : "#fff" }}>
                  <input type="radio" name="attendance" checked={d.attendance === val} onChange={() => setD({ ...d, attendance: val })} className="mt-1 w-5 h-5 accent-[#F59C1A]" />
                  <span className="font-semibold text-sm" style={{ color: C.navy }}>{label}</span>
                </label>
              ))}
            </div>
            {d.attendance === "dropoff" && (
              <div className="grid sm:grid-cols-2 gap-4 mt-5" data-testid="event-emergency-fields">
                <div><label className={lbl}>{tr.emergencyName} *</label><input data-testid="event-emergency-name" className={inputCls} value={d.emergency_name} onChange={(e) => setD({ ...d, emergency_name: e.target.value })} />{showErr && !d.emergency_name.trim() && <p className="text-xs text-red-500 mt-1">{tr.errEmergency}</p>}</div>
                <div><label className={lbl}>{tr.emergencyPhone} *</label><input type="tel" data-testid="event-emergency-phone" className={inputCls} value={d.emergency_phone} onChange={(e) => setD({ ...d, emergency_phone: e.target.value })} /></div>
              </div>
            )}
          </div>

          {/* Section 4 - Allergies */}
          <div className="bg-white rounded-3xl border-2 border-slate-100 shadow-sm p-6 md:p-8">
            <h3 className="font-bold text-lg mb-2" style={{ color: C.green }}>{tr.s4}</h3>
            <p className="text-sm font-semibold text-slate-700 mb-4">{tr.allergyQ} *</p>
            <div className="flex gap-3 mb-5">
              {[["no", tr.no], ["yes", tr.yes]].map(([val, label]) => (
                <button type="button" key={val} data-testid={`event-allergies-${val}`} onClick={() => setD({ ...d, has_allergies: val })}
                  className="px-6 py-2.5 rounded-full text-sm font-bold border-2 transition"
                  style={d.has_allergies === val ? { background: C.green, borderColor: C.green, color: "#fff" } : { borderColor: "#e2e8f0", color: "#475569" }}>{label}</button>
              ))}
            </div>
            {d.has_allergies === "yes" && (
              <div className="mb-5" data-testid="event-allergy-details-wrap">
                <label className={lbl}>{tr.explain} *</label>
                <textarea rows={3} data-testid="event-allergy-details" className={inputCls} value={d.allergy_details} onChange={(e) => setD({ ...d, allergy_details: e.target.value })} />
                {showErr && !d.allergy_details.trim() && <p className="text-xs text-red-500 mt-1">{tr.errAllergy}</p>}
              </div>
            )}
            <div>
              <label className={lbl}>{tr.anythingElse}</label>
              <textarea rows={3} data-testid="event-additional-info" className={inputCls} value={d.additional_info} onChange={(e) => setD({ ...d, additional_info: e.target.value })} />
            </div>
          </div>

          {/* Section 5 - Payment */}
          <div className="bg-white rounded-3xl border-2 shadow-sm p-6 md:p-8" style={{ borderColor: C.yellow }} data-testid="event-payment-section">
            <h3 className="font-bold text-lg mb-5" style={{ color: C.navy }}>{tr.s5}</h3>
            <div className="rounded-2xl p-5 mb-6" style={{ background: "#F0FDFA" }}>
              <div className="flex justify-between text-sm font-semibold text-slate-600 mb-1"><span>{tr.feeLabel}</span><span data-testid="event-fee-breakdown">{tr.perChild(children.length)}</span></div>
              <div className="flex justify-between text-xl font-extrabold" style={{ color: C.navy }}><span>{tr.totalLabel}</span><span data-testid="event-total">${total}.00</span></div>
            </div>

            <label className="flex items-start gap-3 cursor-pointer mb-6" data-testid="event-consent">
              <input type="checkbox" checked={d.consent} onChange={(e) => setD({ ...d, consent: e.target.checked })} className="mt-1 w-5 h-5 accent-[#7A3E9D]" />
              <span className="text-sm font-medium text-slate-700">{tr.consent} *</span>
            </label>

            {eventFull ? (
              <div className="text-center">
                <p className="text-xl font-extrabold text-red-600 mb-2" data-testid="event-full-banner">{tr.full}</p>
                <p className="text-sm text-slate-500 mb-6">{tr.fullNote}</p>
                {status === "error" && <p className="text-red-500 text-sm mb-4">{tr.errGeneric}</p>}
                <button type="button" data-testid="event-waitlist-submit" disabled={status === "sending"} onClick={submitWaitlist}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-2xl px-8 py-5 font-extrabold text-white text-lg shadow-xl disabled:opacity-60" style={{ background: C.orange }}>
                  {status === "sending" ? tr.sending : tr.joinWaitlist}
                </button>
              </div>
            ) : (
              <div>
                <p className="text-center text-xs text-slate-400 mb-5">{tr.payNote}</p>
                {payError && <div className="mb-4 rounded-2xl border-2 border-red-200 bg-red-50 text-red-700 px-5 py-3 text-sm" data-testid="event-pay-error">{payError}</div>}
                <button type="button" data-testid="event-submit-pay" disabled={status === "sending"} onClick={submitRegistration}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-2xl px-8 py-5 font-extrabold text-white text-lg shadow-xl disabled:opacity-60" style={{ background: C.orange }}>
                  <BadgeDollarSign size={22} /> {status === "sending" ? tr.sending : tr.payLabel}
                </button>
                {!valid && showErr && <p className="text-xs text-red-500 text-center mt-3">{tr.errRequired}</p>}
              </div>
            )}
          </div>
        </form>
      </section>
    </div>
  );
}
